import KoalaButton from 'koala/components/Button';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Objective, Outcome, Plan } from 'types';
import pptxgen from 'pptxgenjs';
import queryKeys from 'config/queryKeys';
import { useQuery } from 'react-query';
import * as remoteApi from 'api/remote';
import { CustomTermKey, translate } from 'utils/customTermUtils';
import { shallowEqual, useSelector } from 'react-redux';
import * as outcomeUtils from 'utils/outcomeUtils';
import * as checkinUtils from 'utils/checkinUtils';
import theme from 'theme';
import TabilityWordmarkDark from 'components/_assets/tability_logo_black.png';
import TabilityTitle from 'components/_assets/tability_presentation_bkg.png';
import TabilityBackground from 'components/_assets/tability_presentation_watermark.png';

function htmlToPlainText(html: string) {
  return html
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li>/gi, '• ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '') // Remove all other tags
    .trim();
}

interface Props {
  plan: Plan;
}

function ExportPlanPowerPoint(props: Props) {
  const { plan } = props;
  const { t } = useTranslation();

  const translationKey = 'modals.exportPlan';

  const staleTime = queryKeys.staleTime;

  const currentWorkspace = useSelector((state: any) => state.session.currentWorkspace, shallowEqual);

  // Get the objectives
  const { data: objectivesResponse, isLoading: objectivesAreLoading } = useQuery(
    [queryKeys.objectives, plan.id],
    remoteApi.fetchPlanObjectives,
    {
      staleTime,
    },
  );

  // Get the outcomes
  const { data: outcomesResponse, isLoading: outcomesAreLoading } = useQuery(
    [queryKeys.outcomes, plan.id],
    remoteApi.fetchPlanOutcomes,
    {
      staleTime,
    },
  );

  const objectives = objectivesResponse?.data || [];
  const outcomes = outcomesResponse?.data || [];

  const handleExportCSV = () => {
    // 1. Create a new Presentation
    let pres = new pptxgen();

    // title page
    const slide = pres.addSlide();

    // set bg to tability image
    slide.background = {
      path: TabilityTitle,
    };
    slide.addText(currentWorkspace.name, {
      x: 0.5,
      y: 0.5,
      w: 9,
      h: 1,
      fontSize: 18,
      color: theme.colors.N0,
      bold: true,
      align: 'center',
    });

    slide.addText(plan.title, {
      x: 0.5,
      y: 1.7,
      w: 9,
      h: 2,
      fontSize: 36,
      bold: true,
      align: 'center',
      color: theme.colors.N0,
    });

    slide.addText('Powered by', {
      x: 7.65,
      y: 5.35,
      fontSize: 10,
      color: theme.colors.N0,
    });

    slide.addImage({
      x: 8.5, // image placed just to the right of "Powered by"
      y: 5.2,
      w: 1.12,
      h: 0.29,
      path: TabilityWordmarkDark, // or base64 string
    });

    // for each objective in plan, add a slide
    objectives.forEach((objective: Objective) => {
      let slide = pres.addSlide();
      slide.background = {
        path: TabilityBackground,
      };

      slide.addText(objective.title, { x: 0.2, y: 0, color: theme.colors.N100, h: 1, fontSize: 24, bold: true });

      const objectiveOutcomes = outcomes.filter((outcome: Outcome) => outcome.objective_id === objective.id);

      const tableDetails: pptxgen.TableRow[] = [
        [
          { text: translate(currentWorkspace, CustomTermKey.OUTCOME, 1), options: { bold: true } },
          { text: 'Target', options: { bold: true } },
          { text: 'Current', options: { bold: true } },
          { text: 'Comment', options: { bold: true } },
        ],
      ];
      // for each outcome in objective, add a a row to table
      objectiveOutcomes.forEach((outcome: Outcome, index: number) => {
        let formattedFrom = '';
        let formattedTo = '';
        if (outcome.score_format) {
          formattedFrom = outcome.from || outcome.from === 0 ? outcomeUtils.nFormatter(outcome.from, 2) : '';
          if (outcome.to || outcome.to === 0) {
            formattedTo = outcomeUtils.nFormatter(outcome.to, 2);
            formattedTo = outcome.score_format.replace('_number_', `${formattedTo}`);
          }
        }

        let content = '';
        if (outcome.outcome_type === 'improve_metric') {
          content = `${formattedFrom} ➞ ${formattedTo}`;
        } else if (outcome.outcome_type === 'stay_above') {
          content = `x > ${formattedTo}`;
        } else if (outcome.outcome_type === 'stay_below') {
          content = `x < ${formattedTo}`;
        } else if (outcome.outcome_type === 'kpi') {
          if (outcome.score_format) {
            formattedFrom = formattedFrom ? outcome.score_format.replace('_number_', formattedFrom) : '';
          }
          content = formattedFrom ? `KPI ${formattedFrom}` : '—';
        }

        const currentCheckin = outcome.current_checkin;

        const currentValue = currentCheckin
          ? outcome.score_format
            ? outcome.score_format.replace('_number_', outcomeUtils.nFormatter(currentCheckin.score, 2))
            : outcomeUtils.nFormatter(currentCheckin.score, 2)
          : '-';

        const currentValueColor = currentCheckin?.confidence
          ? checkinUtils.confidenceToProgressColor(currentCheckin.confidence)
          : theme.colors.N0;

        const comment = currentCheckin?.body ? htmlToPlainText(currentCheckin.body) : '-';

        tableDetails.push([
          { text: outcome.title, options: {} },
          { text: content },
          { text: currentValue, options: { fill: { color: currentValueColor } } },
          { text: comment },
        ]);
      });

      slide.addTable(tableDetails, {
        x: 0.3,
        y: 1,
        color: '363636',
        border: { color: '363636' },
        colW: [1.1, 1, 1, 6.3],
        autoPage: true,
      });
    });

    // 4. Save the Presentation
    pres.writeFile({ fileName: `${plan.title}.pptx` });
  };

  return (
    <div>
      <div>
        <KoalaButton onClick={handleExportCSV} appearance="subtle" loading={outcomesAreLoading || objectivesAreLoading}>
          {t(`${translationKey}.button`)}
        </KoalaButton>
      </div>
    </div>
  );
}

export default ExportPlanPowerPoint;
